import { STATUS } from '@btime/constants'
import { appendIf, assocIf } from '@btime/helpers'
import { path } from 'ramda'
import { useLoggedUser, useSessionPermission } from '@btime/react-session'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { formatFieldValues, useFieldsForBulkForm } from '../../../utils/helpers/fields'
import { useFormState } from '../../../packages/kiti/components/form/form/FormHandler'
import { useFormatInitialState } from './helpers/formatInitialState'
import { useGetFieldValueAddressFromApi } from '../../../utils/hooks/fieldValueAddress'
import { useSaveServiceOrders } from './_requests/saveServiceOrders'
import { useServiceOrders } from './_load/ServiceOrdersLoader'
import AddressInput from '../../../components/form/AddressInput'
import AssetSelect from '../../../containers/assets/select/AssetSelect'
import BulkForm from '../../../packages/kiti/components/form/form/BulkForm'
import ChecklistSelect from '../../../containers/checklists/select/ChecklistSelect'
import DateInput from '../../../components/form/date/DateInput'
import Form from '../../../packages/kiti/components/form/form/Form'
import Line from '../../../packages/kiti/components/helpers/Line'
import Loading from '../../../packages/kiti/components/helpers/Loading'
import PlaceSelect from '../../../containers/places/select/PlaceSelect'
import PrioritySelect from '../../../containers/serviceOrders/select/PrioritySelect'
import Scroll from '../../../packages/kiti/components/helpers/Scroll'
import Separator from '../../../packages/kiti/components/helpers/Separator'
import SubmitBar from '../../../packages/kiti/components/form/SubmitBar'
import Textarea from '../../../packages/kiti/components/form/Textarea'
import UsersOrGroupsSelect from '../../../containers/users/select/UsersOrGroupsSelect'
import { isAllEqual } from '../../../utils/helpers/lists'
import { useModalData } from '../../../components/modals/ModalsContext'
import { Col, Grid } from '../../../packages/kiti/components/structure/Grid'
import FormItem from '../../../packages/kiti/components/form/form/FormItem'
import Switch from '../../../components/form/Switch'
import Input from '../../../packages/kiti/components/form/Input'
import { usePlaceTypesWithPermission } from '../../../utils/helpers/placeTypes'
import Select from '../../../packages/kiti/components/form/Select'

const isDisabled = item => !!item.id && ![STATUS.OPEN, STATUS.REQUEST].includes(path(['status', 'name'], item))

const EditServiceOrderForm = ({ parent, initialItemValue, isEditing, disableMultiEdit }) => {
  const { t } = useTranslation('serviceOrders')
  const canUseName = useSessionPermission('editServiceOrdersName')
  const canEditUser = useSessionPermission('editFinishedServiceOrdersUser')
  const canSeeOnlySelfSOs = useSessionPermission('seeOnlySelfServiceOrders')
  const hidePlace = useSessionPermission('hideServiceOrdersPlace')
  const canSelectAsset = useSessionPermission('listAsset') && !hidePlace
  const [fields, { loading: loadingFields }] = useFieldsForBulkForm('service_orders')
  const placeTypes = usePlaceTypesWithPermission('list')

  const { handleChangeIn, form } = useFormState()
  const [getPlaceAddress, { loading: loadingAddress }] = useGetFieldValueAddressFromApi()

  if (loadingFields) return <Loading />

  const afterChangePlace = (place, { path }) => {
    getPlaceAddress(place?.id, place?.type).then(handleChangeIn([...path, 'address']))
    handleChangeIn([...path, 'asset'], undefined)
  }

  const handleAfterChangePlaceType = (_field, { path }) => {
    handleChangeIn([...path, 'place'], undefined)
  }

  const handleParentDependent = value => {
    form.forEach((_item, index) => handleChangeIn([index, 'parentDependent'], value))
  }

  return (
    <Scroll height="69vh" scroll scrollX direction="column">
      <Separator weight={8} flex={1}>
        {!!parent && (
          <Grid maxWidth={'750px'} paddingGutter={0.5} wrap>
            <Col width="30%">
              <FormItem label={t('bulkEdit.openingAsChild')}>
                <Input value={`${parent?.yearlyId} - ${parent?.checklistName}`} disabled />
              </FormItem>
            </Col>

            <Col width="50%">
              <FormItem
                label={t('bulkEdit.makeParentPending')}
                field="parentDependent"
                Input={Switch}
                value={form[0].parentDependent}
                onChange={handleParentDependent}
              />
            </Col>
          </Grid>
        )}
        <BulkForm
          isEditing={isEditing}
          disableMultiEdit={disableMultiEdit}
          initialItemState={initialItemValue}
          fields={[
            ...appendIf(isEditing, { label: t('bulkEdit.fields.code'), name: 'yearlyId', disabled: true }),
            ...appendIf(canUseName, { label: t('bulkEdit.fields.name'), name: 'name' }),
            {
              label: `${t('bulkEdit.fields.checklist')}*`,
              name: 'checklist',
              Input: ChecklistSelect,
              disabled: isDisabled
            },

            ...appendIf(!hidePlace, {
              label: t('bulkEdit.fields.placeType'),
              name: 'placeType',
              Input: Select,
              afterChange: handleAfterChangePlaceType,
              options: placeTypes.map(type => ({ id: type.id, value: type.key, name: type.namePlural }))
            }),

            ...appendIf(!hidePlace, {
              label: `${t('bulkEdit.fields.place')}*`,
              name: 'place',
              Input: PlaceSelect,
              dynamicProps: item => ({ type: item?.placeType?.value }),
              afterChange: afterChangePlace
            }),

            ...appendIf(canSelectAsset, {
              label: t('bulkEdit.fields.asset'),
              name: 'asset',
              Input: AssetSelect,
              dynamicProps: item => ({ filter: { placeIds: item?.place?.id }, disabled: !item?.place?.id })
            }),

            ...appendIf(!canSeeOnlySelfSOs, {
              label: t('bulkEdit.fields.responsible'),
              name: 'userOrGroup',
              Input: UsersOrGroupsSelect,
              disabled: item => !canEditUser && isDisabled(item),
              width: 26,
              dynamicProps: item => ({ address: item.address })
            }),
            {
              label: t('bulkEdit.fields.address'),
              name: 'address',
              Input: AddressInput,
              width: 24,
              loading: loadingAddress
            },
            { label: t('bulkEdit.fields.scheduling'), name: 'scheduling', Input: DateInput, useTime: true },
            { label: t('bulkEdit.fields.priority'), name: 'priority', Input: PrioritySelect, width: 8 },
            { label: t('bulkEdit.fields.description'), name: 'description', Input: Textarea, width: 24 },
            ...fields
          ]}
        />
      </Separator>
    </Scroll>
  )
}

const Wrapper = () => {
  const serviceOrders = useServiceOrders()
  const loggedUser = useLoggedUser()
  const statuses = serviceOrders?.map(item => item?.status?.id)
  const isEditing = !!serviceOrders?.length
  const disableMultiEdit = isEditing && !isAllEqual(statuses)
  const canSeeOnlySelfSOs = useSessionPermission('seeOnlySelfServiceOrders')
  const [onSubmit, { loading, error }] = useSaveServiceOrders()
  const { parent } = useModalData() || {}

  const initialItemValue = {
    parentDependent: false,
    parentId: parent?.id,
    ...assocIf(canSeeOnlySelfSOs, 'userOrGroup', loggedUser),
    fieldValues: formatFieldValues([], 'service_orders')
  }

  const [initialState, state] = useFormatInitialState(initialItemValue)

  if (state.loading) return <Loading />

  return (
    <Form onSubmit={!loading ? onSubmit : undefined} initialState={initialState}>
      <EditServiceOrderForm
        parent={parent}
        initialItemValue={initialItemValue}
        isEditing={isEditing}
        disableMultiEdit={disableMultiEdit}
      />
      <Line />
      <Separator paddingH={2}>
        <SubmitBar loading={loading} error={error} />
      </Separator>
    </Form>
  )
}

export default Wrapper
